import { ReloadIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Grid, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IBallTypeConfig } from 'lib_ts/interfaces/i-ball-types';
import { first } from 'lodash';
import { useEffect, useMemo } from 'react';
import useMiscStore from 'stores/use-misc-store';
import useBallTypesStore from './store/use-ball-types-store';

const PAGE_SIZES = TABLES.PAGE_SIZES.SM;

export const BallTypesTable = () => {
  const configs = useBallTypesStore((s) => s.configs);
  const loading = useBallTypesStore((s) => s.loading);
  const refLists = useBallTypesStore((s) => s.refLists);

  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const calibrationOptions = useMemo<IOption[]>(
    () =>
      refLists
        .filter(
          (l) => l.reference_type === ReferenceListType.BallTypeCalibration
        )
        .map((l) => ({ label: l.name, value: l._id })),
    [refLists]
  );

  const repeatabilityOptions = useMemo<IOption[]>(
    () =>
      refLists
        .filter((l) => l.reference_type === ReferenceListType.Repeatability)
        .map((l) => ({ label: l.name, value: l._id })),
    [refLists]
  );

  const columns = useMemo<ITypedTableColumn<IBallTypeConfig>[]>(
    () => [
      {
        label: 'common.ball-type',
        key: 'ball_type',
        classNameFn: () => 'v-align-top',
        formatFn: (a) => {
          const color = [a.calibrationListID, a.repeatabilityListID].includes(
            undefined
          )
            ? RADIX.COLOR.DANGER
            : undefined;

          return (
            <Box pt="3" pb="3">
              <Text color={color}>{a.ball_type}</Text>
            </Box>
          );
        },
      },
      {
        label: 'Ball Type Calibration List',
        key: 'calibrationListID',
        classNameFn: () => 'v-align-top',
        formatFn: (a) => (
          <Box pt="3" pb="3">
            <CommonSearchInput
              id={`${a._id}-btc`}
              values={a.calibrationListID ? [a.calibrationListID] : []}
              onChange={(values) =>
                useBallTypesStore
                  .getState()
                  .updateConfig(a._id, { calibrationListID: first(values) })
              }
              options={calibrationOptions}
              required
            />
          </Box>
        ),
        disableSort: true,
      },
      {
        label: 'Repeatability Testing List',
        key: 'repeatabilityListID',
        classNameFn: () => 'v-align-top',
        formatFn: (a) => (
          <Box pt="3" pb="3">
            <CommonSearchInput
              id={`${a._id}-repeatability`}
              values={a.repeatabilityListID ? [a.repeatabilityListID] : []}
              onChange={(values) =>
                useBallTypesStore
                  .getState()
                  .updateConfig(a._id, { repeatabilityListID: first(values) })
              }
              options={repeatabilityOptions}
              required
            />
          </Box>
        ),
        disableSort: true,
      },
    ],
    [calibrationOptions, repeatabilityOptions]
  );

  useEffect(() => {
    useBallTypesStore.getState().fetchConfigs();
    useBallTypesStore.getState().fetchRefLists();
  }, []);

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminBallTypeList,
    total: configs.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'ball_type',
      dir: -1,
    },
  };

  return (
    <ErrorBoundary identifier="BallTypesTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <CommonTableHoC
            dataTestID="AdminBallTypes"
            identifier="AdminBallTypesTable"
            toolbarContent={
              <Flex gap={RADIX.FLEX.GAP.SM}>
                <Grid flexGrow="1" columns="5" gap={RADIX.FLEX.GAP.SM}>
                  <Box>
                    <Button
                      onClick={() =>
                        useBallTypesStore.getState().fetchConfigs()
                      }
                    >
                      <ReloadIcon /> Refresh
                    </Button>
                  </Box>
                </Grid>
              </Flex>
            }
            displayColumns={columns}
            displayData={configs}
            loading={loading}
            enableListener={!anyOpenDialogs}
            {...pagination}
            {...sort}
            vFlex
          />
        }
      />
    </ErrorBoundary>
  );
};
